// src/UI/PhoneInput/index.tsx
import React, { useRef, useState, useEffect, ClipboardEvent, KeyboardEvent } from 'react';
import bemClassName from '../../utils/bem';
import './index.scss';

const phoneItem = bemClassName('phone-input');

interface PhoneInputProps {
  inputId?: string;
  text?: string;
  validate?: string;
  onComplete?: (value: string) => void;
  isValidExternal?: boolean;
  externalClassName?: string;
  valueExternal?: string;
  handleExternal?: (value: string) => void;
}

const PHONE_DIGITS = 10;

const PhoneInput: React.FC<PhoneInputProps> = ({
  inputId = 'phoneNumber',
  text = 'Введите номер телефона',
  validate = 'Неверный формат номера',
  isValidExternal = true,
  externalClassName = '',
  valueExternal = '',
  handleExternal = () => {},
  onComplete,
}) => {
  const [phoneValue, setPhoneValue] = useState<string>('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    let cleanValue = valueExternal.replace(/\D/g, '');
    if (cleanValue.startsWith('7') || cleanValue.startsWith('8')) {
      cleanValue = cleanValue.slice(1);
    }
    setPhoneValue(cleanValue.slice(0, PHONE_DIGITS));
  }, [valueExternal]);

  const digitsArray = phoneValue.split('').slice(0, PHONE_DIGITS);
  while (digitsArray.length < PHONE_DIGITS) {
    digitsArray.push('');
  }

  const setInputRef = (index: number, el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  };

  const updatePhoneValue = (newValue: string) => {
    setPhoneValue(newValue);
    handleExternal(newValue);

    if (newValue.length === PHONE_DIGITS && onComplete) {
      onComplete(newValue);
    }
  };

  const handleInputChange = (index: number, value: string) => {
    const enteredDigit = value.replace(/\D/g, '');
    if (!enteredDigit) return;

    const currentDigits = [...digitsArray];
    currentDigits[index] = enteredDigit.slice(-1);

    const newPhoneValue = currentDigits.join('').replace(/\s/g, '').slice(0, PHONE_DIGITS);
    updatePhoneValue(newPhoneValue);

    if (index < PHONE_DIGITS - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      const currentDigits = [...digitsArray];

      if (!currentDigits[index] && index > 0) {
        currentDigits[index - 1] = '';
        const newPhoneValue = currentDigits.join('').replace(/\s/g, '');
        updatePhoneValue(newPhoneValue);
        inputRefs.current[index - 1]?.focus();
      } else {
        currentDigits[index] = '';
        const newPhoneValue = currentDigits.join('').replace(/\s/g, '');
        updatePhoneValue(newPhoneValue);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < PHONE_DIGITS - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    let cleanDigits = pastedData.replace(/\D/g, '');

    if (cleanDigits.length >= 10 && (cleanDigits.startsWith('7') || cleanDigits.startsWith('8'))) {
      cleanDigits = cleanDigits.slice(1);
    }

    const finalValue = cleanDigits.slice(0, PHONE_DIGITS);
    updatePhoneValue(finalValue);

    const focusIndex = finalValue.length < PHONE_DIGITS ? finalValue.length : PHONE_DIGITS - 1;
    inputRefs.current[focusIndex]?.focus();
  };

  const isCompleted = phoneValue.length === PHONE_DIGITS;
  const showError = !isValidExternal && isCompleted;

  const renderInput = (index: number) => (
    <input
      key={index}
      ref={(el) => setInputRef(index, el)}
      id={index === 0 ? inputId : undefined}
      className={phoneItem('digit-input')}
      type="text"
      inputMode="numeric"
      maxLength={1}
      value={digitsArray[index]}
      onChange={(e) => handleInputChange(index, e.target.value)}
      onKeyDown={(e) => handleKeyDown(index, e)}
      onPaste={handlePaste}
      placeholder="0"
    />
  );

  return (
    <div className={phoneItem({ error: showError }, externalClassName)}>
      {text && (
        <label htmlFor={inputId} className={phoneItem('label')}>
          {text}
        </label>
      )}

      <div className={phoneItem('wrapper')}>
        <span className={phoneItem('prefix')}>+7</span>

        <div className={phoneItem('digit-container')}>
          <span className={phoneItem('separator')}>(</span>
          {renderInput(0)}
          {renderInput(1)}
          {renderInput(2)}
          <span className={phoneItem('separator')}>)</span>

          <span className={phoneItem('space')} />

          {renderInput(3)}
          {renderInput(4)}
          {renderInput(5)}

          <span className={phoneItem('separator')}>-</span>
          {renderInput(6)}
          {renderInput(7)}

          <span className={phoneItem('separator')}>-</span>
          {renderInput(8)}
          {renderInput(9)}
        </div>
      </div>

      {showError && <div className={phoneItem('error-message')}>{validate}</div>}
    </div>
  );
};

export default PhoneInput;
